import React, { useMemo, useRef, useState } from 'react';
import { useFormikContext } from 'formik';
import { useTranslation } from 'react-i18next';

import { Arrow } from 'icons/untitledui';
import useClickOutside from 'shared/hooks/useClickOutside';
import * as Dropdown from 'theme/components/SelectionDropdown.style';
import * as Styled from 'form/styles/FormEditView.style';

interface ChoiceFieldProps {
  slug: string;
  label: string;
  options: string[];
}

export default function ChoiceField({
  slug,
  label,
  options,
}: ChoiceFieldProps): JSX.Element {
  const { values, setFieldValue } = useFormikContext<Record<string, any>>();
  const { t } = useTranslation('pageForm');
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useClickOutside([rootRef], () => setOpen(false), open);

  const currentValue = (values[slug] as string) || '';

  const filtered = useMemo(() => {
    const query = currentValue.trim().toLowerCase();
    if (!query) return options;
    const exactMatch = options.some((opt) => opt.toLowerCase() === query);
    if (exactMatch) return options;
    return options.filter((opt) => opt.toLowerCase().includes(query));
  }, [currentValue, options]);

  const handleChange = (next: string) => {
    setFieldValue(slug, next);
  };

  const handleOptionClick = (opt: string) => {
    handleChange(opt);
    setOpen(false);
    inputRef.current?.blur();
  };

  const handleChevronClick = () => {
    setOpen((prev) => !prev);
    inputRef.current?.focus();
  };

  return (
    <Styled.ChoiceFieldRoot ref={rootRef}>
      <Styled.ChoiceFieldTrigger $open={open}>
        <Styled.ChoiceFieldInput
          ref={inputRef}
          id={slug}
          name={slug}
          type="text"
          autoComplete="off"
          value={currentValue}
          aria-label={label}
          aria-expanded={open}
          aria-haspopup="listbox"
          placeholder={t('edit.selectPlaceholder')}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            handleChange(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
            if (e.key === 'Escape') setOpen(false);
          }}
        />
        <Styled.ChoiceFieldChevron
          $open={open}
          type="button"
          aria-label={label}
          onClick={handleChevronClick}
        >
          <Arrow.ChevronDown size={20} />
        </Styled.ChoiceFieldChevron>
      </Styled.ChoiceFieldTrigger>
      {open && filtered.length > 0 && (
        <Dropdown.Popover
          role="listbox"
          aria-label={label}
          $fullWidth
          $openUp={false}
        >
          <Dropdown.List>
            {filtered.map((opt) => {
              const selected = opt === currentValue;
              return (
                <Dropdown.Option
                  key={opt}
                  type="button"
                  role="option"
                  aria-selected={selected}
                  $selected={selected}
                  onClick={() => handleOptionClick(opt)}
                >
                  <Dropdown.OptionContent>
                    <span>{opt}</span>
                  </Dropdown.OptionContent>
                </Dropdown.Option>
              );
            })}
          </Dropdown.List>
        </Dropdown.Popover>
      )}
    </Styled.ChoiceFieldRoot>
  );
}
